import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  MapPin, Home as HomeIcon, ChevronRight,
  Building2, TrendingUp, User, Globe,
  Languages, Clock, Briefcase, Map as MapIcon,
} from 'lucide-react';
import { useJsApiLoader, GoogleMap, MarkerF } from '@react-google-maps/api';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ListingCard } from '@/components/common/ListingCard';
import { EmptyState } from '@/components/common/EmptyState';
import { AgentContactCard } from '@/components/detail/AgentContactCard';
import { getPublicAgentByRef, normalizeListing } from '@/services/api';
import { formatPrice, formatCompactPrice } from '@/utils/format';

const GMAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const GMAPS_LIBRARIES = ['marker'];
const MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };

function humanizePosition(pos = '') {
  return pos.replace(/([A-Z])/g, ' $1').trim();
}

function getInitials(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

const AVATAR_COLORS = [
  'bg-blue-600', 'bg-violet-600', 'bg-emerald-600',
  'bg-rose-600', 'bg-amber-600', 'bg-cyan-600',
];

function getAvatarColor(name = '') {
  const code = name.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
}

function SectionCard({ title, icon: Icon, children, noPad = false }) {
  return (
    <section className="mt-6 rounded-2xl border border-slate-200 bg-white overflow-hidden">
      {title && (
        <div className="flex items-center gap-2 border-b border-slate-100 px-6 py-4">
          {Icon && <Icon size={18} className="text-blue-500 shrink-0" />}
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        </div>
      )}
      <div className={noPad ? '' : 'px-6 py-5'}>{children}</div>
    </section>
  );
}

function InfoRow({ label, value, href }) {
  return (
    <div className="flex items-baseline gap-2 py-1.5">
      <span className="w-44 shrink-0 text-sm font-medium text-slate-700">{label}</span>
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline break-all">
          {value}
        </a>
      ) : (
        <span className="text-sm text-slate-600">{value}</span>
      )}
    </div>
  );
}

function DealCard({ title, rows }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
        <p className="text-sm font-semibold text-slate-800">{title}</p>
      </div>
      <div className="divide-y divide-slate-100">
        {rows.map(({ label, value }) => (
          <div key={label} className="flex items-center justify-between px-4 py-2.5">
            <span className="text-sm text-slate-500">{label}</span>
            <span className="text-sm font-semibold tabular-nums text-slate-900">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const TABS = [
  { key: 'active', label: 'Active' },
  { key: 'sold', label: 'Sold' },
  { key: 'leased', label: 'Leased' },
];

function AgentMap({ listings }) {
  const [selectedListing, setSelectedListing] = useState(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GMAPS_API_KEY || '',
    libraries: GMAPS_LIBRARIES,
    id: 'casahom-gmaps',
  });

  const validListings = useMemo(
    () => listings.filter((l) => l.lat && l.lng),
    [listings],
  );

  const mapOptions = useMemo(() => ({
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    zoomControlOptions: { position: 3 },
  }), []);

  const onMapLoad = useCallback((map) => {
    if (!validListings.length) return;
    if (validListings.length === 1) {
      map.setCenter({ lat: validListings[0].lat, lng: validListings[0].lng });
      map.setZoom(13);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    validListings.forEach((l) => bounds.extend({ lat: l.lat, lng: l.lng }));
    map.fitBounds(bounds, 40);
  }, [validListings]);

  const handleMapClick = useCallback(() => setSelectedListing(null), []);

  if (!GMAPS_API_KEY || loadError || !validListings.length) return null;
  if (!isLoaded) {
    return <div className="h-96 rounded-xl border border-slate-200 bg-slate-100 animate-pulse" />;
  }

  return (
    <div className="relative h-96 rounded-xl border border-slate-200 overflow-hidden mb-5">
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        options={mapOptions}
        onLoad={onMapLoad}
        onClick={handleMapClick}
      >
        {validListings.map((l) => (
          <MarkerF
            key={l.mlsNumber || `${l.lat}-${l.lng}`}
            position={{ lat: l.lat, lng: l.lng }}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: selectedListing?.mlsNumber === l.mlsNumber ? '#FF9500' : '#007AFF',
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 2,
            }}
            onClick={() => setSelectedListing(l)}
          />
        ))}
      </GoogleMap>

      {selectedListing && (
        <div className="absolute bottom-4 left-4 z-10 w-64 rounded-xl bg-white shadow-xl border border-slate-200 overflow-hidden">
          <button
            onClick={() => setSelectedListing(null)}
            className="absolute top-2 right-2 text-slate-400 hover:text-slate-700 text-lg leading-none"
            aria-label="Close"
          >
            ×
          </button>
          {selectedListing.photos?.[0] && (
            <img
              src={selectedListing.photos[0]}
              alt={selectedListing.streetAddress}
              className="w-full h-32 object-cover"
            />
          )}
          <div className="p-3">
            <p className="font-semibold text-slate-900 text-sm">
              {formatPrice(selectedListing.soldPrice ?? selectedListing.price, selectedListing.isRental)}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              {[
                selectedListing.beds && `${selectedListing.beds} Beds`,
                selectedListing.baths && `${selectedListing.baths} Baths`,
                selectedListing.sqft && `${selectedListing.sqft} Sq Ft`,
              ].filter(Boolean).join(' · ')}
            </p>
            <p className="text-xs text-slate-600 mt-1 truncate">
              {selectedListing.streetAddress || selectedListing.address}
              {selectedListing.city ? `, ${selectedListing.city}` : ''}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AgentProfile() {
  const { ref } = useParams();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('active');
  const contactRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    setLoading(true);
    getPublicAgentByRef(ref).then((data) => {
      setAgent(data);
      setLoading(false);
    });
  }, [ref]);

  const derived = useMemo(() => {
    if (!agent) {
      return {
        activeListings: [], soldListings: [], leasedListings: [],
        closedSales: null, aboutText: '',
        homeTypes: [], salesStats: null, rentalsStats: null, neighborhoods: [],
        forSaleCount: 0, soldCount: 0, leasedCount: 0,
      };
    }
    const listings = agent.listings || {};
    const activeListings = (listings.active || []).map(normalizeListing);
    const soldListings = (listings.sold || []).map(normalizeListing);
    const leasedListings = (listings.leased || []).map(normalizeListing);
    const allListings = [...activeListings, ...soldListings, ...leasedListings];

    // Closed sales bar (prior 5 years)
    const fiveYearsAgo = new Date();
    fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);
    const qualifiedSold = soldListings
      .map((l) => ({ ...l, effectivePrice: l.soldPrice ?? l.price }))
      .filter((l) => {
        if (l.effectivePrice <= 0) return false;
        if (!l.soldDate) return true;
        return new Date(l.soldDate) >= fiveYearsAgo;
      });
    let closedSales = null;
    if (qualifiedSold.length > 0) {
      const prices5 = qualifiedSold.map((l) => l.effectivePrice);
      const total = prices5.reduce((a, b) => a + b, 0);
      closedSales = {
        count: qualifiedSold.length,
        total,
        min: Math.min(...prices5),
        max: Math.max(...prices5),
        avg: Math.round(total / qualifiedSold.length),
      };
    }

    // Counts
    const forSaleCount = Math.max(agent.active_count || 0, activeListings.length);
    const soldCount = Math.max(agent.sold_count || 0, soldListings.length);
    const leasedCount = leasedListings.length;

    // About text
    const areasServedSet = new Set();
    allListings.forEach((l) => {
      if (l.city) areasServedSet.add([l.city, l.state].filter(Boolean).join(', '));
    });
    const areasServed = [...areasServedSet].sort();
    const parts = [];
    if (agent.brokerage_name) parts.push(`works with ${agent.brokerage_name}`);
    if (areasServed.length > 0) parts.push(`serves areas including ${areasServed.slice(0, 3).join(', ')}`);
    const counts = [];
    if (forSaleCount > 0) counts.push(`${forSaleCount} active listing${forSaleCount !== 1 ? 's' : ''}`);
    if (soldCount > 0) counts.push(`${soldCount} sold`);
    if (leasedCount > 0) counts.push(`${leasedCount} leased`);
    if (counts.length > 0) parts.push(`has ${counts.join(', ')}`);
    const aboutText = agent.bio || (parts.length > 0 ? `${agent.name} ${parts.join(', ')}.` : '');

    // Home types
    const typeSet = new Set();
    allListings.forEach((l) => { if (l.type) typeSet.add(l.type); });
    const homeTypes = [...typeSet].sort();

    // Transaction history — Sales
    const saleItems = soldListings
      .map((l) => l.soldPrice ?? l.price)
      .filter((p) => p > 0);
    let salesStats = null;
    if (saleItems.length > 0) {
      const total = saleItems.reduce((a, b) => a + b, 0);
      salesStats = {
        count: saleItems.length,
        total,
        avg: Math.round(total / saleItems.length),
        min: Math.min(...saleItems),
        max: Math.max(...saleItems),
      };
    }

    // Transaction history — Rentals
    const rentalItems = leasedListings
      .map((l) => l.soldPrice ?? l.price)
      .filter((p) => p > 0);
    let rentalsStats = null;
    if (rentalItems.length > 0) {
      const total = rentalItems.reduce((a, b) => a + b, 0);
      rentalsStats = {
        count: rentalItems.length,
        total,
        avg: Math.round(total / rentalItems.length),
      };
    }

    // Neighborhoods
    const neighborhoodMap = new Map();
    allListings.forEach((l) => {
      const n = (l.neighborhood || '').trim();
      if (!n) return;
      neighborhoodMap.set(n, (neighborhoodMap.get(n) || 0) + 1);
    });
    const neighborhoods = [...neighborhoodMap.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ name, count }));

    return {
      activeListings, soldListings, leasedListings,
      forSaleCount, soldCount, leasedCount,
      closedSales, aboutText,
      homeTypes, salesStats, rentalsStats, neighborhoods,
    };
  }, [agent]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Skeleton className="h-4 w-32 mb-6" />
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-start gap-5">
            <Skeleton className="h-24 w-24 rounded-full shrink-0" />
            <div className="flex-1 space-y-3">
              <Skeleton className="h-7 w-1/2" />
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-4 w-2/5" />
              <div className="flex gap-2 pt-1">
                <Skeleton className="h-6 w-24 rounded-full" />
                <Skeleton className="h-6 w-16 rounded-full" />
              </div>
            </div>
          </div>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="overflow-hidden rounded-2xl bg-white border border-slate-200 shadow-md">
              <Skeleton className="aspect-[4/3] w-full" />
              <div className="p-5 space-y-3">
                <Skeleton className="h-6 w-1/2" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <EmptyState
          icon={HomeIcon}
          title="Agent not found"
          description="This agent profile doesn't exist or has been removed."
          actionLabel="Back to agents"
          onAction={() => window.history.back()}
        />
      </div>
    );
  }

  const {
    activeListings, soldListings, leasedListings,
    forSaleCount, soldCount, leasedCount,
    closedSales, aboutText,
    homeTypes, salesStats, rentalsStats, neighborhoods,
  } = derived;

  const firstName = agent.name ? agent.name.split(' ')[0] : agent.name;
  const initials = getInitials(agent.name);
  const avatarColor = getAvatarColor(agent.name);
  const location = [agent.brokerage_city, agent.brokerage_state].filter(Boolean).join(', ');
  const tabData = { active: activeListings, sold: soldListings, leased: leasedListings };
  const currentListings = tabData[tab] || [];
  const visibleTabs = TABS.filter((t) => tabData[t.key].length > 0 || t.key === 'active');

  const scrollToContact = () => {
    contactRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const stats = [
    { key: 'sale', label: 'For sale', value: forSaleCount },
    soldCount > 0 && { key: 'sold', label: 'Sold', value: soldCount },
    leasedCount > 0 && { key: 'leased', label: 'Leased', value: leasedCount },
    neighborhoods.length > 0 && { key: 'neighborhoods', label: 'Neighborhoods', value: neighborhoods.length },
  ].filter(Boolean);

  const statsGridCols = (
    { 1: 'sm:grid-cols-1', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-4', 5: 'sm:grid-cols-5' }
  )[Math.min(stats.length, 5)] || 'sm:grid-cols-4';

  const showTransactionHistory = salesStats || rentalsStats;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Breadcrumb */}
      <nav className="mb-4 flex items-center gap-1 text-sm text-slate-500" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-blue-600" aria-label="Home">
          <HomeIcon size={14} />
        </Link>
        <ChevronRight size={14} />
        <Link to="/agents" className="hover:text-blue-600">Agents</Link>
        <ChevronRight size={14} />
        <span className="truncate text-slate-700">{agent.name}</span>
      </nav>

      {/* Hero card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row items-start gap-5">
          <div
            className={`h-24 w-24 shrink-0 rounded-full ${avatarColor} flex items-center justify-center text-white font-bold text-3xl select-none`}
            aria-hidden="true"
          >
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl font-bold text-slate-900">{agent.name}</h1>
            {agent.position && (
              <p className="mt-1 text-slate-500">{humanizePosition(agent.position)}</p>
            )}
            {agent.brokerage_name && (
              <p className="mt-0.5 font-medium text-slate-700">{agent.brokerage_name}</p>
            )}
            {location && (
              <p className="mt-1 flex items-center gap-1 text-sm text-slate-500">
                <MapPin size={14} className="shrink-0 text-slate-400" />
                {location}
              </p>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700">
                {forSaleCount} active
              </span>
              {soldCount > 0 && (
                <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-600">
                  {soldCount} sold
                </span>
              )}
              {leasedCount > 0 && (
                <span className="inline-flex items-center rounded-full bg-emerald-50 border border-emerald-100 px-3 py-1 text-sm font-medium text-emerald-700">
                  {leasedCount} leased
                </span>
              )}
            </div>
          </div>
          <Button onClick={scrollToContact} className="rounded-full shrink-0 sm:self-start mt-2 sm:mt-0">
            Contact agent
          </Button>
        </div>
      </div>

      {/* Stats row */}
      {stats.length > 0 && (
        <div className={`mt-4 grid gap-3 grid-cols-2 ${statsGridCols}`}>
          {stats.map((s) => (
            <div key={s.key} className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{s.label}</p>
              <p className="mt-1 truncate text-lg font-bold text-slate-900">{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Closed sales stat bar */}
      {closedSales && (
        <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-lg font-bold text-slate-900">{closedSales.count}</p>
              <p className="text-sm text-slate-500">Closed Sales</p>
            </div>
            <div>
              <p className="text-lg font-bold text-slate-900">{formatCompactPrice(closedSales.total)}</p>
              <p className="text-sm text-slate-500">Total Value</p>
            </div>
            <div>
              <p className="text-lg font-bold text-slate-900">
                {closedSales.min === closedSales.max
                  ? formatCompactPrice(closedSales.min)
                  : `${formatCompactPrice(closedSales.min)} – ${formatCompactPrice(closedSales.max)}`}
              </p>
              <p className="text-sm text-slate-500">Price Range</p>
            </div>
            <div>
              <p className="text-lg font-bold text-slate-900">{formatCompactPrice(closedSales.avg)}</p>
              <p className="text-sm text-slate-500">Average Price</p>
            </div>
          </div>
          <p className="mt-2 text-right text-xs text-slate-400">Prior 5 years</p>
        </div>
      )}

      {/* Two-column layout */}
      <div className="mt-6 lg:grid lg:grid-cols-[1fr_360px] lg:gap-8">
        {/* Left column */}
        <div>

          {/* 1. About */}
          <SectionCard title={`About ${firstName}`} icon={User}>
            {aboutText && (
              <p className="leading-relaxed text-slate-600 mb-4">{aboutText}</p>
            )}
            <div className="divide-y divide-slate-100">
              {agent.position && (
                <InfoRow label="Credential" value={humanizePosition(agent.position)} />
              )}
              {homeTypes.length > 0 && (
                <InfoRow label="Home Types" value={homeTypes.join(', ')} />
              )}
              {agent.languages && (
                <InfoRow label="Languages Spoken" value={agent.languages} />
              )}
              {agent.years_experience && (
                <InfoRow label="Years of Experience" value={agent.years_experience} />
              )}
              {agent.website && (
                <InfoRow label="Agent Website" value={agent.website} href={agent.website} />
              )}
            </div>
          </SectionCard>

          {/* 2. Transaction History */}
          {showTransactionHistory && (
            <SectionCard title="Transaction History" icon={TrendingUp}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {salesStats ? (
                  <DealCard
                    title="Sales"
                    rows={[
                      { label: 'Total Deals', value: salesStats.count },
                      { label: 'Total Value', value: formatPrice(salesStats.total) },
                      { label: 'Average Sale Price', value: formatPrice(salesStats.avg) },
                      {
                        label: 'Price Range',
                        value: salesStats.min === salesStats.max
                          ? formatCompactPrice(salesStats.min)
                          : `${formatCompactPrice(salesStats.min)} – ${formatCompactPrice(salesStats.max)}`,
                      },
                    ]}
                  />
                ) : (
                  <DealCard title="Sales" rows={[{ label: 'Total Deals', value: 0 }]} />
                )}
                {rentalsStats ? (
                  <DealCard
                    title="Rentals"
                    rows={[
                      { label: 'Total Deals', value: rentalsStats.count },
                      { label: 'Total Value', value: formatPrice(rentalsStats.total) },
                      { label: 'Average Price', value: formatPrice(rentalsStats.avg) },
                    ]}
                  />
                ) : (
                  <DealCard title="Rentals" rows={[{ label: 'Total Deals', value: 0 }]} />
                )}
              </div>
            </SectionCard>
          )}

          {/* 3. Neighborhoods */}
          {neighborhoods.length > 0 && (
            <SectionCard title={`Neighborhoods ${firstName} is Experienced In`} icon={MapIcon}>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
                {neighborhoods.map(({ name, count }) => (
                  <div key={name}>
                    <p className="text-sm font-medium text-blue-700">{name}</p>
                    <p className="text-xs text-amber-600 mt-0.5">
                      {count} Listing{count !== 1 ? 's' : ''}
                    </p>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {/* 4. Listings + Map */}
          <SectionCard title={`${firstName}'s Listings and Deals`} icon={Building2} noPad>
            <div className="flex gap-1 border-b border-slate-100 px-6 pt-4">
              {visibleTabs.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
                    tab === t.key
                      ? 'text-blue-700 border-b-2 border-blue-600 bg-blue-50'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                >
                  {t.label}
                  {tabData[t.key].length > 0 && (
                    <span className="ml-1.5 text-xs text-slate-400">({tabData[t.key].length})</span>
                  )}
                </button>
              ))}
            </div>
            <div className="px-6 py-5">
              <AgentMap listings={currentListings} />
              {currentListings.length === 0 ? (
                <EmptyState
                  title={`No ${tab} listings`}
                  description={`This agent has no ${tab} listings at the moment.`}
                />
              ) : (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {currentListings.map((l) => (
                    <ListingCard key={l.id || l.mlsNumber} listing={l} />
                  ))}
                </div>
              )}
            </div>
          </SectionCard>

        </div>

        {/* Right sidebar */}
        <aside ref={contactRef} className="mt-6 lg:mt-0">
          <AgentContactCard agent={agent} />
        </aside>
      </div>
    </div>
  );
}
